import type { BannerConfig, Regime } from './types.js';
import { effectiveLang, labelOf, strings } from './ui.js';

/**
 * 배너가 **보여 주는 문안 전문** — 명세 §2-1 의 2.
 *
 * ## 왜 필요한가
 *
 * RAYConsent 의 기록은 **등록된 문구**를 가리킨다 (설계 불변 조건 1). 배너
 * 문안을 문구로 등록하고, 기록할 때마다 **이 함수가 만든 문안의 해시**를 같이
 * 보낸다. 수신 측은 등록본 중 해시가 같은 판을 찾아 그 판으로 기록하고,
 * 없으면 **적재하지 않는다.**
 *
 * 그래서 이 함수는 **화면과 같은 재료**(`strings`·`labelOf`)로 만든다. 따로
 * 적어 두면 화면만 고치고 이것을 안 고치는 날이 온다 — 그러면 「본 문장」과
 * 「기록된 문장」이 갈린다. 현행 위탁 서비스가 더미 문구로 실패한 지점이다.
 *
 * ## 두 층을 다 담는다
 *
 * 배너는 첫 화면(요약·버튼)과 항목별 선택 화면 두 층이다. 「모두 허용」은
 * 첫 화면에서 누를 수 있고 그것이 동의하는 대상은 **목적 목록 전체**다. 그래서
 * 한 판에 두 층을 모두 담는다 — 이용자에게 제시된 것 전부다.
 *
 * ## 등록 문구의 바이트가 이것과 같아야 한다
 *
 * 줄바꿈·공백 하나가 달라도 해시가 다르다. 등록은 반드시
 * `tools/notice-text.mjs` 가 낸 출력을 그대로 쓴다.
 */
export interface Notice {
  /** 문구 id. CCPA 는 본문이 달라 **다른 문구**다. */
  readonly textId: string;
  /** 실제로 그린 언어. 없는 언어는 영어로 떨어진다 — 그 떨어진 값이다. */
  readonly locale: string;
  readonly text: string;
  /** 필수 목적 id. 동의 대상이 아니라 기록에서 뺀다. */
  readonly essential: readonly string[];
}

export const DEFAULT_TEXT_ID = 'cookie-banner';

export function noticeFor(config: BannerConfig, lang: string, regime: Regime): Notice {
  const locale = effectiveLang(lang);
  const s = strings(locale);
  const base = config.noticeTextId ?? DEFAULT_TEXT_ID;

  const lines: string[] = [s.title, '', regime === 'ccpa' ? `${s.body} ${s.ccpaNote}` : s.body];

  const url = config.policyUrl[lang] ?? Object.values(config.policyUrl)[0];
  if (url !== undefined) lines.push('', `${s.policy}: ${url}`);

  lines.push('');
  for (const p of config.purposes) {
    lines.push(`- ${labelOf(p, locale)}${p.essential === true ? ` (${s.essential})` : ''}`);
    const desc = p.description?.[locale];
    if (desc !== undefined) lines.push(`  ${desc}`);
  }

  lines.push('', [s.acceptAll, s.rejectAll, s.customize, s.save].map((b) => `[${b}]`).join(' '));

  return {
    textId: regime === 'ccpa' ? `${base}-ccpa` : base,
    locale,
    text: `${lines.join('\n')}\n`,
    essential: config.purposes.filter((p) => p.essential === true).map((p) => p.id),
  };
}

/** UTF-8 sha256, 소문자 hex. RAYConsent 가 문구에 매기는 값과 같은 정의다. */
export async function sha256Hex(text: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(buf), (b) => b.toString(16).padStart(2, '0')).join('');
}
